import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CardsList } from '@/components/Profile/Cards/CardsList';
import { CardsEmpty } from '@/components/Profile/Cards/CardsEmpty';
import { TechIssues } from '@/components/Profile/Cards/TechIssues';
import { Spinner } from '@/components/Spinner/Spinner';
import { ErrorIcon, SuccessFilledIcon } from '@/components/UI/Icons/Icons';
import { useToastStore } from '@/store/useToastStore';
import { useUserCards } from './hooks';
import type { CardNotifyVariant } from './hooks';
import * as S from './PaymentMethodsSection.styles';

const NOTIFY_ICON = {
  success: <SuccessFilledIcon width={20} height={20} />,
  error: <ErrorIcon width={20} height={20} />,
} as const;

export const PaymentMethodsSection = () => {
  const { t } = useTranslation();
  const { openToast } = useToastStore();

  const handleNotify = useCallback(
    (message: string, variant: CardNotifyVariant) => {
      openToast({
        text: message,
        icon: NOTIFY_ICON[variant],
        size: 'compact',
        borderRadius: 14,
      });
    },
    [openToast]
  );

  const {
    cards,
    isLoading,
    isError,
    addCard,
    updateCard,
    deleteCard,
    setPrimaryCard,
  } = useUserCards({ onNotify: handleNotify });

  const handleRenameCard = useCallback(
    (id: number, name: string, onSuccess?: () => void) => {
      updateCard(id, { name }, onSuccess);
    },
    [updateCard]
  );

  if (isLoading && !cards.length) {
    return (
      <S.LoadingBox>
        <Spinner size="m" />
      </S.LoadingBox>
    );
  }

  if (isError) {
    return <TechIssues />;
  }

  return (
    <>
      {cards.length ? (
        <CardsList
          cards={cards}
          onAddCard={addCard}
          onRenameCard={handleRenameCard}
          onDeleteCard={deleteCard}
          onMakePrimary={setPrimaryCard}
        />
      ) : (
        <CardsEmpty onAddCard={addCard} />
      )}
      <S.AgreementText buttonName={t('pages.cabinet.add_new_card')} />
    </>
  );
};
